import { IOSTemplateDto } from 'components/OSTemplates/types/dtos';
import { useAppSelector } from 'hooks';
import { Currency } from 'hooks/money-formatter';
import React from 'react';
import { DiskType, IParamLimits } from 'store/virtual-machine/types/state.types';

interface ILimits {
  min: number,
  max: number,
  // doesn't depend on ratio, depends only on vm count
  totalMax: number,
  price: number
  currency: Currency | null
}

export interface IVMCreateLimits {
  cpu: ILimits
  ram: ILimits
  disk: ILimits
}

export type ReservedParams = {
  cpu: number
  ram: number
  ssd_disk: number
  hdd_disk: number
  nvme_disk: number
};

function calculateCommonLimits(
  params: IParamLimits,
  diskType: DiskType,
  cpu: number,
  vmCount: number,
): IVMCreateLimits {
  const limits = {
    cpu: {
      min: 0,
      max: 0,
      totalMax: 0,
      price: 0,
      currency: params.cpu_cores?.currencyCode || null,
    },
    ram: {
      min: 0,
      max: 0,
      totalMax: 0,
      price: 0,
      currency: params.ram?.currencyCode || null,
    },
    disk: {
      min: 0,
      max: 0,
      totalMax: 0,
      price: 0,
      currency: params.hdd_size?.currencyCode || null,
    },
  };

  limits.ram.min = Math.max(
    Math.ceil(cpu * (params.ram?.minRatio || 0)),
    params.ram?.min || 1,
  );

  limits.ram.max = Math.min(
    Math.floor(cpu * (params.ram?.maxRatio || 999)),
    Math.min(
      params.ram?.max || 256,
      params.ram?.limit || 256,
    ),
  );
  limits.ram.totalMax = Math.floor((params.ram?.limit || 256) / vmCount);
  limits.ram.price = params.ram?.price || 0;

  if (diskType === DiskType.HDD) {
    limits.disk.min = params.hdd_size?.min || 10;
    limits.disk.max = Math.min(
      params.hdd_size?.max || 100,
      params.hdd_size?.limit || 100,
    );
    limits.disk.totalMax = Math.floor((params.hdd_size?.limit || 100) / vmCount);
    limits.disk.price = params.hdd_size?.price || 0;
    limits.disk.currency = params.hdd_size?.currencyCode || null;
  }

  if (diskType === DiskType.SSD) {
    limits.disk.min = params.ssd_size?.min || 10;
    limits.disk.max = Math.min(
      params.ssd_size?.max || 100,
      params.ssd_size?.limit || 100,
    );
    limits.disk.totalMax = Math.floor((params.ssd_size?.limit || 100) / vmCount);
    limits.disk.price = params.ssd_size?.price || 0;
    limits.disk.currency = params.ssd_size?.currencyCode || null;
  }
  if (diskType === DiskType.NVME) {
    limits.disk.min = params.nvme_size?.min || 10;
    limits.disk.max = Math.min(
      params.nvme_size?.max || 100,
      params.nvme_size?.limit || 100,
    );
    limits.disk.totalMax = Math.floor((params.nvme_size?.limit || 100) / vmCount);
    limits.disk.price = params.nvme_size?.price || 0;
  }

  limits.cpu.min = params.cpu_cores?.min || 1;
  limits.cpu.max = Math.min(
    params.cpu_cores?.max || Infinity,
    params.cpu_cores?.limit || 1,
  );
  limits.cpu.totalMax = Math.floor((params.cpu_cores?.limit || 100) / vmCount);
  limits.cpu.price = params.cpu_cores?.price || 0;

  return limits;
}

function calculateCustomTemplateLimits(
  params: IParamLimits,
  customTemplate: IOSTemplateDto,
  diskType: DiskType,
  cpu: number,
): IVMCreateLimits {
  const limits = {
    cpu: {
      min: 0,
      max: 0,
      totalMax: params.cpu_cores?.limit || 100,
      price: params.cpu_cores?.price || 0,
      currency: params.cpu_cores?.currencyCode || null,

    },
    ram: {
      min: 0,
      max: 0,
      totalMax: params.ram?.limit || 256,
      price: params.ram?.price || 0,
      currency: params.ram?.currencyCode || null,

    },
    disk: {
      min: 0,
      max: 0,
      totalMax: 0,
      price: 0,
      currency: params.hdd_size?.currencyCode || null,

    },
  };

  limits.cpu.max = Math.min(
    (params.cpu_cores?.max || Infinity),
    params.cpu_cores?.limit || 1,
  );

  limits.cpu.min = Math.min(customTemplate.minSizeCpu
    || params.cpu_cores?.min || 1, limits.cpu.max);

  limits.ram.max = Math.min(
    Math.floor(cpu * (params.ram?.maxRatio || Infinity)),
    Math.min(params.ram?.max || 256, params.ram?.limit || 256),
  );

  limits.ram.min = Math.min(Math.max(
    Math.ceil(cpu * (params.ram?.minRatio || 0)),
    customTemplate.minSizeRam || params.ram?.min || 1,
  ), limits.ram.max);

  if (diskType === DiskType.HDD) {
    limits.disk.min = customTemplate.minSizeGb || params.hdd_size?.min || 10;
    limits.disk.max = Math.min(
      params.hdd_size?.max || 100,
      params.hdd_size?.limit || 100,
    );
    limits.disk.price = params.hdd_size?.price || 0;
    limits.disk.totalMax = params.hdd_size?.limit || 100;
    limits.disk.currency = params.hdd_size?.currencyCode || null;
  }
  if (diskType === DiskType.SSD) {
    limits.disk.min = customTemplate.minSizeGb || params.ssd_size?.min || 10;
    limits.disk.max = Math.min(
      params.ssd_size?.max || 100,
      params.ssd_size?.limit || 100,
    );
    limits.disk.price = params.ssd_size?.price || 0;
    limits.disk.totalMax = params.ssd_size?.limit || 100;
    limits.disk.currency = params.ssd_size?.currencyCode || null;
  }
  if (diskType === DiskType.NVME) {
    limits.disk.min = customTemplate.minSizeGb || params.nvme_size?.min || 10;
    limits.disk.max = Math.min(
      params.nvme_size?.max || 100,
      params.nvme_size?.limit || 100,
    );
    limits.disk.price = params.nvme_size?.price || 0;
    limits.disk.totalMax = params.nvme_size?.limit || 100;
  }

  return limits;
}

export type CalculateLimits =
  (value: {
    templateParams?: IParamLimits | null
    customTemplate?: IOSTemplateDto | null
    diskType: DiskType
    cpu: number
    vmCount?: number
    reserved?: ReservedParams
  }) => IVMCreateLimits;

export default function useVMCreateLimits(
  initialDiskType: DiskType,
  initialCpu?: number,
  initialVmCount?: number,
): [
    IVMCreateLimits,
    CalculateLimits,
  ] {
  const globalLimits = useAppSelector((state) => state.virtualMachine.paramLimits);

  const [resultLimits, setResultLimits] = React.useState<IVMCreateLimits>(calculateCommonLimits(
    globalLimits,
    initialDiskType,
    initialCpu || globalLimits.cpu_cores?.min || 1,
    initialVmCount || 1,
  ));

  const calculateLimits: CalculateLimits = ({
    templateParams, customTemplate, cpu, diskType, vmCount, reserved,
  }) => {
    let limits = { ...resultLimits };

    const params: IParamLimits = templateParams ? JSON.parse(JSON.stringify(templateParams))
      : JSON.parse(JSON.stringify(globalLimits));

    if (reserved) {
      if (params.cpu_cores?.limit) {
        params.cpu_cores.limit -= reserved.cpu;
      }
      if (params.ram?.limit) {
        params.ram.limit -= reserved.ram;
      }
      if (params.ssd_size?.limit) {
        params.ssd_size.limit -= reserved.ssd_disk;
      }
      if (params.hdd_size?.limit) {
        params.hdd_size.limit -= reserved.hdd_disk;
      }
      if (params.nvme_size?.limit) {
        params.nvme_size.limit -= reserved.nvme_disk;
      }
    }

    if (customTemplate) {
      limits = calculateCustomTemplateLimits(params, customTemplate, diskType, cpu);
      setResultLimits(limits);
      return limits;
    }

    limits = calculateCommonLimits(params, diskType, cpu, vmCount || 1);

    setResultLimits(limits);
    return limits;
  };

  return [resultLimits, calculateLimits];
}
